import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { creditService, CreditHistoryQueryParams, CreateDemoRequest } from '@/services';
import { CreditOperationRequest } from '@/types/model';

export const creditKeys = {
  all: ['credits'] as const,
  history: () => [...creditKeys.all, 'history'] as const,
  historyByUser: (username: string) => [...creditKeys.history(), username] as const,
  historyByUserParams: (username: string, params: CreditHistoryQueryParams) => 
    [...creditKeys.historyByUser(username), params] as const,
  historyAll: (params: CreditHistoryQueryParams) => [...creditKeys.history(), 'all', params] as const,
  recentHistory: (username: string) => [...creditKeys.history(), username, 'recent'] as const,
  operationsCount: (username: string) => [...creditKeys.all, 'count', username] as const,
  demoPurchase: () => [...creditKeys.all, 'demo', 'purchase'] as const,
};

/**
 * Hook for getting credit history for a specific user
 */
export const useCreditHistory = (username: string, params: CreditHistoryQueryParams = {}) => {
  return useQuery({
    queryKey: creditKeys.historyByUserParams(username, params),
    queryFn: () => creditService.getCreditHistory(username, params),
    enabled: !!username,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for getting recent credit history for a user (last 50 records)
 */
export const useRecentCreditHistory = (username: string) => {
  return useQuery({
    queryKey: creditKeys.recentHistory(username),
    queryFn: () => creditService.getRecentCreditHistory(username),
    enabled: !!username,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook for getting all credit history (admin only)
 */
export const useAllCreditHistory = (params: CreditHistoryQueryParams = {}) => {
  return useQuery({
    queryKey: creditKeys.historyAll(params),
    queryFn: () => creditService.getAllCreditHistory(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for getting credit operations count for a user
 */
export const useCreditOperationsCount = (username: string) => {
  return useQuery({
    queryKey: creditKeys.operationsCount(username),
    queryFn: () => creditService.getCreditOperationsCount(username),
    enabled: !!username,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for adding normal credits
 */
export const useAddCredits = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreditOperationRequest) =>
      creditService.addCredits(data),
    onSuccess: (_, variables) => {
      // Invalidate credit history for the target user
      queryClient.invalidateQueries({ 
        queryKey: creditKeys.historyByUser(variables.targetUsername) 
      });
      queryClient.invalidateQueries({ 
        queryKey: creditKeys.recentHistory(variables.targetUsername) 
      });
      queryClient.invalidateQueries({ 
        queryKey: creditKeys.operationsCount(variables.targetUsername) 
      });
      // Invalidate all credit history for admin views
      queryClient.invalidateQueries({ 
        queryKey: creditKeys.history() 
      });
      // Invalidate user list to update credits display
      queryClient.invalidateQueries({ 
        queryKey: ['users'] 
      });
    },
  });
};

/**
 * Hook for adding demo credits
 */
export const useAddDemoCredits = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateDemoRequest) =>
      creditService.addDemoCredits(data),
    onSuccess: (_, variables) => {
      const targetUsername = variables.targetUsername || String(variables.userId);
      queryClient.invalidateQueries({
        queryKey: creditKeys.historyByUser(targetUsername)
      });
      queryClient.invalidateQueries({
        queryKey: creditKeys.recentHistory(targetUsername)
      });
      queryClient.invalidateQueries({
        queryKey: creditKeys.operationsCount(targetUsername)
      });
      queryClient.invalidateQueries({
        queryKey: creditKeys.history()
      });
      queryClient.invalidateQueries({
        queryKey: ['users']
      });
    },
  });
};

/**
 * Hook for deducting normal credits
 */
export const useDeductCredits = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreditOperationRequest) =>
      creditService.deductCredits(data),
    onSuccess: (_, variables) => {
      // Invalidate credit history for the target user
      queryClient.invalidateQueries({ 
        queryKey: creditKeys.historyByUser(variables.targetUsername) 
      });
      queryClient.invalidateQueries({ 
        queryKey: creditKeys.recentHistory(variables.targetUsername) 
      });
      queryClient.invalidateQueries({ 
        queryKey: creditKeys.operationsCount(variables.targetUsername) 
      });
      // Invalidate all credit history for admin views
      queryClient.invalidateQueries({ 
        queryKey: creditKeys.history() 
      });
      // Invalidate user list to update credits display
      queryClient.invalidateQueries({ 
        queryKey: ['users'] 
      });
    },
  });
};

/**
 * Hook for adding sale credits to agents
 */
export const useAddCreditsForSale = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreditOperationRequest) =>
      creditService.addCreditsForSale(data),
    onSuccess: (_, variables) => {
      // Invalidate credit history for the target agent
      queryClient.invalidateQueries({ 
        queryKey: creditKeys.historyByUser(variables.targetUsername) 
      });
      queryClient.invalidateQueries({ 
        queryKey: creditKeys.recentHistory(variables.targetUsername) 
      });
      queryClient.invalidateQueries({ 
        queryKey: creditKeys.operationsCount(variables.targetUsername) 
      });
      // Invalidate all credit history for admin views
      queryClient.invalidateQueries({ 
        queryKey: creditKeys.history() 
      });
      // Invalidate user list to update saleCredits display
      queryClient.invalidateQueries({ 
        queryKey: ['users'] 
      });
    },
  });
};

/**
 * Hook for deducting sale credits from agents
 */
export const useDeductCreditsForSale = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreditOperationRequest) =>
      creditService.deductCreditsForSale(data),
    onSuccess: (_, variables) => {
      // Invalidate credit history for the target agent
      queryClient.invalidateQueries({ 
        queryKey: creditKeys.historyByUser(variables.targetUsername) 
      });
      queryClient.invalidateQueries({ 
        queryKey: creditKeys.recentHistory(variables.targetUsername) 
      });
      queryClient.invalidateQueries({ 
        queryKey: creditKeys.operationsCount(variables.targetUsername) 
      });
      // Invalidate all credit history for admin views
      queryClient.invalidateQueries({ 
        queryKey: creditKeys.history() 
      });
      // Invalidate user list to update saleCredits display
      queryClient.invalidateQueries({ 
        queryKey: ['users'] 
      });
    },
  });
};

/**
 * Hook for purchasing demo credits
 */
export const usePurchaseDemoCredits = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => creditService.purchaseDemoCredits(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: creditKeys.history()
      });
      queryClient.invalidateQueries({
        queryKey: ['users']
      });
    },
  });
};
